import { sum } from "lodash";
import { makeAutoObservable } from "mobx";

import {
  CALORIES_PER_CARBOHYDRATE_GRAM,
  CALORIES_PER_FAT_GRAM,
  CALORIES_PER_PROTEIN_GRAM,
  FOOD_EXCHANGE_MULTIPLIERS,
} from "@/config/constants";
import { MealPlanAttributes } from "@/gql/graphql";
import { round } from "@/utils/number";
import { toNum } from "@/utils/string";

import MealPlanFormStore from "./MealPlanFormStore";

export default class FoodExchange {
  use = false;

  // To make working with html inputs easier, we'll mainly update these as
  // strings and manually convert them to numbers during calculations.
  values = {
    vegetables: "",
    treats: "",
    starches: "",
    dairy: "",
    meatAndSeafood: "",
    fatsAndOil: "",
    fruits: "",
    plantBasedMeat: "",
  };

  private store: MealPlanFormStore;

  get valuesAsEntries(): [keyof FoodExchange["values"], string][] {
    return Object.entries(this.values) as any;
  }

  get calculations() {
    let totalCalories = 0;
    let totalCarbohydrateGrams = 0;
    let totalProteinGrams = 0;
    let totalFatGrams = 0;

    this.valuesAsEntries.forEach(([fel, value]) => {
      totalCalories += +value * FOOD_EXCHANGE_MULTIPLIERS[fel].calories;
      totalCarbohydrateGrams +=
        +value * FOOD_EXCHANGE_MULTIPLIERS[fel].carbohydrates;
      totalProteinGrams += +value * FOOD_EXCHANGE_MULTIPLIERS[fel].proteins;
      totalFatGrams += +value * FOOD_EXCHANGE_MULTIPLIERS[fel].fats;
    });

    const totalCarbohydrateCalories =
      totalCarbohydrateGrams * CALORIES_PER_CARBOHYDRATE_GRAM;
    const totalProteinCalories = totalProteinGrams * CALORIES_PER_PROTEIN_GRAM;
    const totalFatCalories = totalFatGrams * CALORIES_PER_FAT_GRAM;

    const totalCarbohydratePercent = this.getTotalPercentage(
      totalCarbohydrateCalories,
      this.store.macronutrients.carbohydrateCalories,
      this.store.macronutrients.carbohydratePercent
    );
    const totalProteinPercent = this.getTotalPercentage(
      totalProteinCalories,
      this.store.macronutrients.proteinCalories,
      this.store.macronutrients.proteinPercent
    );
    const totalFatPercent = this.getTotalPercentage(
      totalFatCalories,
      this.store.macronutrients.fatCalories,
      this.store.macronutrients.fatPercent
    );

    return {
      totalCalories,
      totalCarbohydrateGrams,
      totalProteinGrams,
      totalFatGrams,
      totalCarbohydrateCalories,
      totalProteinCalories,
      totalFatCalories,
      totalCarbohydratePercent,
      totalProteinPercent,
      totalFatPercent,
    };
  }

  get autosuggest() {
    const { carbohydrateGrams, proteinGrams, fatGrams } =
      this.store.macronutrients;

    const result = {
      starches: 0,
      meatAndSeafood: 0,
      fatsAndOil: 0,
    };

    console.log("meat ", this.values.meatAndSeafood);

    const vegetables = +this.values.vegetables;
    const vegetablesValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.vegetables.carbohydrates * vegetables,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.vegetables.proteins * vegetables,
      fats: FOOD_EXCHANGE_MULTIPLIERS.vegetables.fats * vegetables,
    };

    const treats = +this.values.treats;
    const treatsValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.treats.carbohydrates * treats,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.treats.proteins * treats,
      fats: FOOD_EXCHANGE_MULTIPLIERS.treats.fats * treats,
    };

    const starches = +this.values.starches;
    const starchesValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.starches.carbohydrates * starches,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.starches.proteins * starches,
      fats: FOOD_EXCHANGE_MULTIPLIERS.starches.fats * starches,
    };

    const dairy = +this.values.dairy;
    const dairyValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.dairy.carbohydrates * dairy,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.dairy.carbohydrates * dairy,
      fats: FOOD_EXCHANGE_MULTIPLIERS.dairy.fats * dairy,
    };

    const meatAndSeafood = +this.values.meatAndSeafood;
    const meatAndSeafoodValues = {
      carbs:
        FOOD_EXCHANGE_MULTIPLIERS.meatAndSeafood.carbohydrates * meatAndSeafood,
      proteins:
        FOOD_EXCHANGE_MULTIPLIERS.meatAndSeafood.proteins * meatAndSeafood,
      fats: FOOD_EXCHANGE_MULTIPLIERS.meatAndSeafood.fats * meatAndSeafood,
    };

    const fatsAndOil = +this.values.fatsAndOil;
    const fatsAndOilValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.fatsAndOil.carbohydrates * fatsAndOil,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.fatsAndOil.carbohydrates * fatsAndOil,
      fats: FOOD_EXCHANGE_MULTIPLIERS.fatsAndOil.fats * fatsAndOil,
    };

    const fruits = +this.values.fruits;
    const fruitsValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.fruits.carbohydrates * fruits,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.fruits.proteins * fruits,
      fats: FOOD_EXCHANGE_MULTIPLIERS.fruits.fats * fruits,
    };

    const plantBasedMeat = +this.values.plantBasedMeat;
    const plantBasedMeatValues = {
      carbs:
        FOOD_EXCHANGE_MULTIPLIERS.plantBasedMeat.carbohydrates * plantBasedMeat,
      proteins:
        FOOD_EXCHANGE_MULTIPLIERS.plantBasedMeat.proteins * plantBasedMeat,
      fats: FOOD_EXCHANGE_MULTIPLIERS.plantBasedMeat.fats * plantBasedMeat,
    };

    const STARCH_MULTIPLIERS = FOOD_EXCHANGE_MULTIPLIERS.starch;

    const totalStarch = starches;

    const totalMeat = meatAndSeafood;

    const totalFat = fatsAndOil;

    /**
     * Calculate starch first.
     *
     * Renamed to rice
     *
     * After adding vegetable, fruit, milk and sugar,
     * get the sum of Carbohydrates
     */

    const dairyCarbohydrates = dairyValues.carbs;

    const totalCarbohydrates = sum([
      vegetablesValues.carbs,
      fruitsValues.carbs,
      treatsValues.carbs,
      dairyCarbohydrates,
    ]);

    const _suggestedStarch = this.suggestFoodExchange(
      carbohydrateGrams,
      totalCarbohydrates,
      STARCH_MULTIPLIERS.carbohydrates
    );

    const suggestedStarch = Math.max(0, _suggestedStarch - totalStarch);

    result.starches = round(suggestedStarch, 1);

    result.starches = round(1);

    /**
     * Calculate meat
     *
     * After finalizing the number of exchanges for the Starch/Grains,
     * this time get the sum of total Protein content.
     * From Vegetable to Starch/Grains Food Groups
     */

    const starchProteins = starchesValues.proteins;
    const dairyProteins = dairyValues.proteins;
    const totalProteins = sum([
      vegetablesValues.proteins,
      fruitsValues.proteins,
      treatsValues.proteins,
      dairyProteins,
      starchProteins,
    ]);

    const _suggestedMeatandSeaFood = this.suggestFoodExchange(
      proteinGrams,
      totalProteins,
      FOOD_EXCHANGE_MULTIPLIERS.meatAndSeafood.proteins
    );

    const suggestedMeatAndSeafood = Math.max(
      0,
      _suggestedMeatandSeaFood - totalMeat
    );

    result.meatAndSeafood = round(suggestedMeatAndSeafood, 1);
    /**
     *  Calculate fat
     *
     *  Then same procedure, after finalizing the number of exchanges for the Meat,
     *  this time get the sum of total Fat content.
     *  From Dairy/Milk to Meat Food Groups
     */

    const riceFats = starchesValues.fats;
    const milkFats = dairyValues.fats;
    const meatFats = meatAndSeafoodValues.fats;
    const totalFats = sum([
      vegetablesValues.fats,
      fruitsValues.fats,
      treatsValues.fats,
      riceFats,
      milkFats,
      meatFats,
    ]);

    const _suggestedFatsAndOil = this.suggestFoodExchange(
      fatGrams,
      totalFats,
      FOOD_EXCHANGE_MULTIPLIERS.fatsAndOil.fats
    );

    const suggestedFatsAndOil = Math.max(0, _suggestedFatsAndOil - totalFat);

    result.fatsAndOil = round(suggestedFatsAndOil, 1);

    return result;
  }

  constructor(store: MealPlanFormStore) {
    makeAutoObservable(this);
    this.store = store;
  }

  toggle(use: boolean): void {
    this.use = use;
  }

  set(name: keyof FoodExchange["values"], value: string): void {
    this.values[name] = value;
  }

  toAttributes(): Partial<MealPlanAttributes> {
    if (!this.use) return {};
    return {
      foodExchangeVegetables: toNum(this.values.vegetables),
      foodExchangeTreats: toNum(this.values.treats),
      foodExchangeStarches: toNum(this.values.starches),
      foodExchangeDairy: toNum(this.values.dairy),
      foodExchangeMeatAndSeafood: toNum(this.values.meatAndSeafood),
      foodExchangeFatsAndOil: toNum(this.values.fatsAndOil),
      foodExchangeFruits: toNum(this.values.fruits),
      foodExchangePlantBasedMeat: toNum(this.values.plantBasedMeat),
    };
  }

  private getTotalPercentage(
    calories: number,
    totalCalories: number,
    maxPercentage: number
  ) {
    if (maxPercentage === 0) return 0;
    // `|| 1` is there to prevent division by zero.
    const _result = calories / (totalCalories || 1);
    return _result * maxPercentage;
  }

  private suggestFoodExchange(
    macroInGrams: number,
    totalFelMacroGrams: number,
    gramsPerExchange: number
  ) {
    const result = (macroInGrams - totalFelMacroGrams) / gramsPerExchange;

    if (isNaN(result) || result < 0) return 0;

    return result;
  }
}
